import { useCallback, useEffect, useRef } from 'react';
import { Divider, Popover, Stack } from '@mui/material';
import { CitationPopoverProps } from './CitationPopover.props';
import CitationCard from '../../Cards/citation-card/CitationCard';

const CitationPopover = ({
  entries,
  columnarIdentifier,
  onDelete,
  onInteractionActiveChange,
  onView,
  slotProps,
  ...popoverProps
}: CitationPopoverProps) => {
  const paperSlotProps = typeof slotProps?.paper === 'function' ? undefined : slotProps?.paper;
  const callerPaperSx = paperSlotProps?.sx;
  const activeInteractionsRef = useRef(new Set<React.Key>());

  const handleInteractionActiveChange = useCallback(
    (entryKey: React.Key, active: boolean) => {
      const wasActive = activeInteractionsRef.current.size > 0;
      if (active) {
        activeInteractionsRef.current.add(entryKey);
      } else {
        activeInteractionsRef.current.delete(entryKey);
      }
      const isActive = activeInteractionsRef.current.size > 0;
      if (isActive !== wasActive) {
        onInteractionActiveChange?.(isActive);
      }
    },
    [onInteractionActiveChange]
  );

  useEffect(
    () => () => {
      if (activeInteractionsRef.current.size > 0) {
        onInteractionActiveChange?.(false);
      }
    },
    [onInteractionActiveChange]
  );

  return (
    <Popover
      {...popoverProps}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      disableRestoreFocus
      slotProps={{
        ...slotProps,
        paper: {
          ...paperSlotProps,
          sx: [
            {
              maxWidth: '400px',
              maxHeight: '70vh',
              overflowY: 'auto',
              padding: 2,
            },
            ...(Array.isArray(callerPaperSx) ? callerPaperSx : [callerPaperSx]),
          ],
        },
      }}>
      <Stack divider={<Divider flexItem />} spacing={2}>
        {entries.map(({ citation, aiCreated }, index) => {
          const entryKey = citation.id ?? `${citation.document_reference}-${citation.field_name}-${index}`;
          return (
            <CitationCard
              key={entryKey}
              citation={citation}
              identifier={columnarIdentifier}
              aiCreated={aiCreated}
              onDelete={onDelete}
              onInteractionActiveChange={active => handleInteractionActiveChange(entryKey, active)}
              onView={onView}
            />
          );
        })}
      </Stack>
    </Popover>
  );
};

export default CitationPopover;
